import type { ConsentKitConfig, DeepPartial, Dictionary, LocaleCode } from "../types";
import { de } from "./locales/de";
import { en } from "./locales/en";

/**
 * Adding a language:
 *  1. Create src/i18n/locales/<code>.ts exporting a Dictionary (copy de.ts).
 *  2. Register it in `LOCALES` and `dictionaries` below.
 * The Dictionary type guarantees no key is forgotten.
 */
export const LOCALES: readonly LocaleCode[] = ["de", "en"] as const;
export const DEFAULT_LOCALE: LocaleCode = "de";

const dictionaries: Record<LocaleCode, Dictionary> = { de, en };

/**
 * Config wins, then the document's own language, then the browser, then `fallback`.
 *
 * The fallback is a parameter rather than a constant because it is region-dependent: a US
 * site whose visitor sends no usable language header should land on English, not on the
 * German this library defaults to everywhere else.
 */
export function resolveLocale(
  configured: ConsentKitConfig["locale"],
  fallback: LocaleCode = DEFAULT_LOCALE,
): LocaleCode {
  if (configured !== "auto") return configured;

  const fromDocument = normalize(document.documentElement.getAttribute("lang"));
  if (fromDocument) return fromDocument;

  const fromNavigator = normalize(navigator.language);
  if (fromNavigator) return fromNavigator;

  return fallback;
}

export function getDictionary(locale: LocaleCode, overrides?: DeepPartial<Dictionary>): Dictionary {
  const base = dictionaries[locale] ?? dictionaries[DEFAULT_LOCALE];
  return overrides ? mergeDictionary(base, overrides) : base;
}

/** Replaces `{name}` placeholders — the only interpolation the dictionaries need. */
export function format(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match);
}

function normalize(value: string | null): LocaleCode | null {
  if (!value) return null;
  const code = value.toLowerCase().split("-")[0];
  return LOCALES.includes(code as LocaleCode) ? (code as LocaleCode) : null;
}

function mergeDictionary(base: Dictionary, overrides: DeepPartial<Dictionary>): Dictionary {
  return deepMerge(base, overrides) as Dictionary;
}

function deepMerge<T extends object>(base: T, patch: DeepPartial<T>): T {
  const out = { ...base };
  for (const key of Object.keys(patch) as (keyof T)[]) {
    const value = patch[key];
    if (value === undefined) continue;
    const current = out[key];
    if (isPlainObject(current) && isPlainObject(value)) {
      out[key] = deepMerge(current, value as DeepPartial<typeof current>);
    } else {
      out[key] = value as T[keyof T];
    }
  }
  return out;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
